// Reusable product info modal.
// Wrap any group of cards + a shared modal shell in <info-modal>. A click on a
// [data-info-trigger] clones the trigger card's <template data-info-modal> into
// the shell and opens it. Keeps all rendering in Liquid; adds focus-trap, ESC,
// overlay-click close, and body scroll lock.
if (!customElements.get("info-modal")) {
  customElements.define(
    "info-modal",
    class InfoModal extends HTMLElement {
      constructor() {
        super();
        this.overlay = this.querySelector("[data-info-overlay]");
        this.box = this.querySelector("[data-info-box]");
        this.content = this.querySelector("[data-info-content]");
        this.closeBtn = this.querySelector("[data-info-close]");
        this.lastFocused = null;

        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onOverlayClick = this.onOverlayClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.closeModal = this.closeModal.bind(this);
      }

      connectedCallback() {
  this.addEventListener("click", this.onTriggerClick);
  this.closeBtn?.addEventListener("click", this.closeModal);
  this.overlay?.addEventListener("click", this.onOverlayClick);
  this.addEventListener('add-to-box:added', () => this.closeModal()); // ← add this
}


      disconnectedCallback() {
        this.removeEventListener("click", this.onTriggerClick);
        this.closeBtn?.removeEventListener("click", this.closeModal);
        this.overlay?.removeEventListener("click", this.onOverlayClick);
        document.removeEventListener("keydown", this.onKeydown);
        document.documentElement.classList.remove("overflow-hidden");
      }

      onTriggerClick(e) {
        const trigger = e.target.closest("[data-info-trigger]");
        if (!trigger || !this.contains(trigger)) return;
        // Don't let the trigger bubble into card actions (e.g. add-to-box).
        e.preventDefault();
        e.stopPropagation();
        const card = trigger.closest("[data-info-card]") || trigger;
        const tpl = card.querySelector("template[data-info-modal]");
        if (!tpl) return;
        this.openModal(tpl);
      }

      openModal(tpl) {
        if (!this.overlay) return;
        this.lastFocused = document.activeElement;
        this.content.replaceChildren(tpl.content.cloneNode(true));
        this.overlay.hidden = false;
        requestAnimationFrame(() => this.overlay.classList.add("is-open"));
        document.documentElement.classList.add("overflow-hidden");
        document.addEventListener("keydown", this.onKeydown);
        this.box?.focus();
      }

      closeModal() {
        if (!this.overlay) return;
        this.overlay.classList.remove("is-open");
        document.documentElement.classList.remove("overflow-hidden");
        document.removeEventListener("keydown", this.onKeydown);
        const hide = () => {
          this.overlay.hidden = true;
          this.content.replaceChildren();
        };
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          hide();
        } else {
          this.overlay.addEventListener("transitionend", hide, { once: true });
        }
        if (this.lastFocused) {
    this.lastFocused.focus({ preventScroll: true });
  }
}

      onOverlayClick(e) {
        if (e.target === this.overlay) this.closeModal();
      }

      onKeydown(e) {
        if (e.key === "Escape") {
          this.closeModal();
        } else if (e.key === "Tab") {
          this.trapFocus(e);
        }
      }

      trapFocus(e) {
        const focusable = this.box.querySelectorAll(
          'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  );
}
